import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { DozentService } from '../../../services/dozent.service';
import {
  AnchorKind,
  Learning,
  LearningInput,
  LearningType,
} from '../../../services/learning.service';

/**
 * Create / edit one Agent Learning. Used from both scopes — the agent page
 * and the client-wide page; `scope` only adds the template selection, which
 * is meaningless for a learning that already belongs to one agent.
 *
 * `prefill` starts a NEW learning from a proposal (curation from a
 * conversation): the form is filled, and the provenance fields
 * (`source_event_id`, `expert_note`, `analysis_category`) travel along to the
 * create call untouched — they are not editable here.
 *
 * Required-field feedback follows CLAUDE.md rule 16: the save button stays
 * clickable, `required` on the controls, the form's `submitted` flag makes
 * RequiredFieldFeedbackDirective paint what is missing, and the global Enter
 * handling can jump to it.
 */
@Component({
  selector: 'app-learning-dialog',
  templateUrl: './learning-dialog.component.html',
  styleUrl: './learning-dialog.component.scss',
})
export class LearningDialogComponent implements OnInit {
  @Input() learning: Learning | null = null;
  @Input() scope: 'client' | 'bot' = 'bot';
  @Input() prefill: Partial<LearningInput> | null = null;
  @Input() onConfirm: (input: LearningInput) => void = () => {};

  /** Backend `endpoints.code_reference` values of the templates that carry
   *  `agent_learnings_enabled` (migration 103). Never translated — they are
   *  mapping keys; labels come from `enums.assistantTemplate`. */
  readonly templateOptions = [
    'alpha_tutor',
    'alpha_flexible',
    'alpha_dozent',
    'alpha_assist',
    'alpha_support',
    'tutor_basic',
  ];

  /** Backend cap (BOT_LEARNING_MAX_ANCHORS). */
  readonly maxAnchors = 12;

  model: LearningInput = {
    type: 'rule',
    topic: '',
    question: '',
    content: '',
    tags: [],
    is_active: true,
    always_apply: false,
    applies_to_templates: null,
    course_id: null,
    valid_until: null,
    review_at: null,
  };

  tagsText = '';
  anchorsText = '';
  allTemplates = true;
  courses: { id: string; name: string }[] = [];

  /** Kind of every anchor we were given, by text — lines the expert keeps
   *  keep their kind ('question' / 'paraphrase'); new lines are 'manual'. */
  private anchorKinds = new Map<string, AnchorKind>();

  constructor(
    protected dialogRef: NbDialogRef<LearningDialogComponent>,
    private dozentService: DozentService
  ) {}

  ngOnInit(): void {
    const source: Partial<LearningInput> | null = this.learning ?? this.prefill;
    if (source) {
      this.model = {
        type: source.type ?? 'rule',
        topic: source.topic || '',
        question: source.question || '',
        content: source.content || '',
        tags: [...(source.tags || [])],
        is_active: source.is_active ?? true,
        always_apply: source.always_apply ?? false,
        applies_to_templates: source.applies_to_templates ? [...source.applies_to_templates] : null,
        course_id: source.course_id ?? null,
        valid_until: source.valid_until ?? null,
        review_at: source.review_at ?? null,
      };
      this.tagsText = (source.tags || []).join(', ');
      this.allTemplates = !source.applies_to_templates?.length;
      for (const a of source.anchors || []) {
        this.anchorKinds.set(this.anchorKey(a.text), a.kind);
      }
      this.anchorsText = (source.anchors || []).map((a) => a.text).join('\n');
    }
    this.loadCourses();
  }

  get isEdit(): boolean {
    return !!this.learning?.id;
  }

  get isExample(): boolean {
    return this.model.type === 'qa_example';
  }

  /** Read-only provenance shown above the form. */
  get expertNote(): string | null {
    return this.learning?.expert_note ?? this.prefill?.expert_note ?? null;
  }

  get fromConversation(): boolean {
    return !!(this.learning?.source_event_id ?? this.prefill?.source_event_id);
  }

  get anchorCount(): number {
    return this.parseAnchors().length;
  }

  get tooManyAnchors(): boolean {
    return this.anchorCount > this.maxAnchors;
  }

  onTypeChange(value: LearningType): void {
    this.model.type = value;
  }

  onAllTemplatesChange(checked: boolean): void {
    this.allTemplates = checked;
    if (checked) {
      this.model.applies_to_templates = null;
    } else if (!this.model.applies_to_templates) {
      this.model.applies_to_templates = [];
    }
  }

  onTemplatesChange(values: string[]): void {
    this.model.applies_to_templates = values?.length ? values : [];
  }

  /** "Only these templates" with nothing ticked would mean "no agent" —
   *  refuse it instead of saving a learning that never applies. */
  get templateSelectionEmpty(): boolean {
    return (
      this.scope === 'client' &&
      !this.allTemplates &&
      !(this.model.applies_to_templates || []).length
    );
  }

  submit(form: NgForm): void {
    if (form.invalid || this.templateSelectionEmpty || this.tooManyAnchors) {
      form.form.markAllAsTouched();
      return;
    }
    const tags = this.tagsText
      .split(',')
      .map((t) => t.trim())
      .filter((t) => !!t);
    const input: LearningInput = {
      type: this.model.type,
      topic: (this.model.topic || '').trim() || null,
      // Rules carry no question — never send a stale one along.
      question: this.isExample ? (this.model.question || '').trim() : null,
      content: this.model.content.trim(),
      tags,
      is_active: this.model.is_active,
      // Only a rule can hold on every turn; an example needs its question.
      always_apply: !this.isExample && !!this.model.always_apply,
      // Client scope sends [] for "every template", never null: the update
      // is a PATCH where null means "unchanged", so a restricted learning
      // could otherwise never be opened up again. The backend stores [] as
      // NULL. Agent scope has no template restriction at all.
      applies_to_templates:
        this.scope === 'client'
          ? this.allTemplates
            ? []
            : this.model.applies_to_templates
          : null,
      // Always sent, null included: for these fields null CLEARS.
      anchors: this.parseAnchors(),
      course_id: this.model.course_id || null,
      valid_until: this.model.valid_until || null,
      review_at: this.model.review_at || null,
    };
    if (!this.isEdit && this.prefill) {
      input.source_event_id = this.prefill.source_event_id ?? null;
      input.expert_note = this.prefill.expert_note ?? null;
      input.analysis_category = this.prefill.analysis_category ?? null;
    }
    this.onConfirm(input);
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  private parseAnchors() {
    const seen = new Set<string>();
    return this.anchorsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => {
        const key = this.anchorKey(line);
        if (!line || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((text) => ({
        text,
        kind: this.anchorKinds.get(this.anchorKey(text)) ?? ('manual' as AnchorKind),
      }));
  }

  private anchorKey(text: string): string {
    return text.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  }

  private loadCourses(): void {
    this.dozentService.listCourses().subscribe({
      next: (rows: any[]) => {
        this.setCourses(
          (rows || [])
            .map((c) => ({ id: String(c.id), name: String(c.course_name || c.name || c.id) }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      },
      // Without the list the field only offers "every course" plus the
      // current value — the learning stays editable.
      error: () => this.setCourses([]),
    });
  }

  /** Keeps a stored course selectable even when the list lacks it (deleted
   *  course, list failed) — otherwise the select would show nothing and a
   *  save would look like it cleared the course. */
  private setCourses(list: { id: string; name: string }[]): void {
    const current = this.model.course_id;
    if (current && !list.some((c) => c.id === current)) {
      list = [{ id: current, name: current }, ...list];
    }
    this.courses = list;
  }
}
